import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid
} from '@mui/material';
import { api } from '../services/api';
import { SelectChangeEvent } from '@mui/material/Select';

interface Measurement {
  id: string;
  zone_id: string;
  date: string;
  time: string;
  ec_coir_bag: string;
  moisture_vmc: string;
  coir_bag_temp: string;
  air_temp: string;
  sbd: string;
  cycles: string;
  drip_volume: string;
  drip_ec: string;
  drip_ph: string;
  drain_volume: string;
  drain_ec: string;
  drain_ph: string;
  run_off_percentage: string;
  notes: string;
  user_id: string;
  farm_id: string;
}

interface Farm {
  id: string;
  name: string;
}

interface Zone {
  id: string;
  name: string;
  farm_id: string;
}

const DataEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [farms, setFarms] = useState<Farm[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedFarm, setSelectedFarm] = useState<Farm | null>(null);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[]>([
    {
      id: crypto.randomUUID(),
      zone_id: selectedZone || '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toISOString().split('T')[1].split('.')[0],
      ec_coir_bag: '',
      moisture_vmc: '',
      coir_bag_temp: '',
      air_temp: '',
      sbd: '',
      cycles: '',
      drip_volume: '',
      drip_ec: '',
      drip_ph: '',
      drain_volume: '',
      drain_ec: '',
      drain_ph: '',
      run_off_percentage: '',
      notes: '',
      user_id: user?.id || '',
      farm_id: selectedFarm ? selectedFarm.id : ''
    }
  ]);

  useEffect(() => {
    const fetchFarms = async () => {
      if (!user) return;
      try {
        const { data } = await api.farms.getAll();
        setFarms(data || []);
      } catch (err) {
        setFarms([]);
      }
    };
    fetchFarms();
  }, [user]);

  useEffect(() => {
    const fetchZones = async () => {
      if (!selectedFarm) return;
      try {
        const { data } = await api.zones.getAll(selectedFarm.id);
        setZones(data || []);
      } catch (err) {
        console.error('Error fetching zones:', err);
      }
    };
    fetchZones();
  }, [selectedFarm]);

  const handleFarmChange = (event: SelectChangeEvent) => {
    const selectedFarmId = event.target.value as string;
    const selectedFarm = farms.find(f => f.id === selectedFarmId);
    setSelectedFarm(selectedFarm || null);
    setSelectedZone(''); // Reset zone when farm changes
    setZones([]); // Clear zones when farm changes
  };

  const handleZoneChange = (event: SelectChangeEvent<string>): void => {
    const newZoneId = event.target.value as string;
    setSelectedZone(newZoneId);
    setMeasurements((prev) => {
      console.log('Updating zone_id:', newZoneId);
      return prev.map(m => ({ ...m, zone_id: newZoneId }));
    });
  };

  const handleInputChange = (idx: number, field: keyof Measurement, value: string): void => {
    setMeasurements((prev) => prev.map((m, i) =>
      i === idx ? { ...m, [field]: value } : m
    ));
  };

  const handleSave = async () => {
    // Validate required fields
    const hasErrors = measurements.some((m, idx) => {
      console.log('Validating measurement:', idx, m);
      console.log('zone_id:', m.zone_id, 'date:', m.date, 'time:', m.time);
      console.log('zone_id type:', typeof m.zone_id);
      console.log('date type:', typeof m.date);
      console.log('time type:', typeof m.time);
      
      // Check if zone_id is empty string or undefined
      const hasZone = m.zone_id && m.zone_id.trim() !== '';
      const hasDate = m.date && m.date.trim() !== '';
      const hasTime = m.time && m.time.trim() !== '';
      
      console.log('Validation:', { hasZone, hasDate, hasTime });
      
      if (!hasZone) {
        console.log('Zone validation failed:', m.zone_id);
      }
      if (!hasDate) {
        console.log('Date validation failed:', m.date);
      }
      if (!hasTime) {
        console.log('Time validation failed:', m.time);
      }
      
      return !hasZone || !hasDate || !hasTime;
    });

    if (hasErrors) {
      console.log('Validation failed');
      alert('Please select a zone and fill in date and time before saving.');
      return;
    }

    try {
      // Ensure we have a valid token before sending
      const token = localStorage.getItem('access_token');

      // Format the measurements data before sending
      const formattedMeasurements = measurements.map(m => ({
        zone_id: m.zone_id,
        date: m.date,
        time: m.time,
        ec_coir_bag: parseFloat(m.ec_coir_bag) || 0,
        moisture_vmc: parseFloat(m.moisture_vmc) || 0,
        coir_bag_temp: parseFloat(m.coir_bag_temp) || 0,
        air_temp: parseFloat(m.air_temp) || 0,
        sbd: parseFloat(m.sbd) || 0,
        cycles: parseInt(m.cycles) || 0,
        drip_volume: parseFloat(m.drip_volume) || 0,
        drip_ec: parseFloat(m.drip_ec) || 0,
        drip_ph: parseFloat(m.drip_ph) || 0,
        drain_volume: parseFloat(m.drain_volume) || 0,
        drain_ec: parseFloat(m.drain_ec) || 0,
        drain_ph: parseFloat(m.drain_ph) || 0,
        run_off_percentage: parseFloat(m.run_off_percentage) || 0,
        notes: m.notes || '',
        user_id: user?.id || ''  // Get user_id from auth context
      }));

      // Add detailed logging of the data being sent
      console.log('=== Measurement Submission Data ===');
      console.log('Selected Farm:', selectedFarm);
      console.log('Selected Zone:', selectedZone);
      console.log('Available Zones:', zones);
      console.log('Available Farms:', farms);
      console.log('User:', user);
      console.log('Formatted Measurements:', formattedMeasurements);
      console.log('Token:', token ? 'Present' : 'Missing');
      console.log('');
      console.log('=== First Measurement Details ===');
      if (formattedMeasurements.length > 0) {
        const firstMeasurement = formattedMeasurements[0];
        console.log('Zone ID:', firstMeasurement.zone_id);
        console.log('User ID:', firstMeasurement.user_id);
        console.log('Date:', firstMeasurement.date);
        console.log('Time:', firstMeasurement.time);
        console.log('EC Coir Bag:', firstMeasurement.ec_coir_bag);
        console.log('Moisture VMC:', firstMeasurement.moisture_vmc);
        console.log('Coir Bag Temp:', firstMeasurement.coir_bag_temp);
        console.log('Air Temp:', firstMeasurement.air_temp);
        console.log('SBD:', firstMeasurement.sbd);
        console.log('Cycles:', firstMeasurement.cycles);
        console.log('Drip Volume:', firstMeasurement.drip_volume);
        console.log('Drip EC:', firstMeasurement.drip_ec);
        console.log('Drip pH:', firstMeasurement.drip_ph);
        console.log('Drain Volume:', firstMeasurement.drain_volume);
        console.log('Drain EC:', firstMeasurement.drain_ec);
        console.log('Drain pH:', firstMeasurement.drain_ph);
        console.log('Run Off %:', firstMeasurement.run_off_percentage);
        console.log('Notes:', firstMeasurement.notes);
      }
      if (!token) {
        alert('Please login first');
        return;
      }

      try {
        // Add detailed logging of the data being sent
        console.log('Attempting to save measurements:', formattedMeasurements);
        console.log('User ID:', user?.id);
        console.log('Selected Zone:', formattedMeasurements[0].zone_id);
        console.log('Date:', formattedMeasurements[0].date);
        console.log('Time:', formattedMeasurements[0].time);

        // Save measurements using the API service
        await Promise.all(formattedMeasurements.map((m) => api.measurements.create(m)));
        navigate('/dashboard');
      } catch (err: any) {
        console.error('Error saving measurements:', err);
        if (err.message) {
          alert(`Failed to save measurements: ${err.message}`);
        } else {
          alert('Failed to save measurements. Please check all required fields are filled.');
        }
      }
    } catch (err: any) {
      console.error('Error saving measurements:', err);
      if (err.message) {
        alert(`Failed to save measurements: ${err.message}`);
      } else {
        alert('Failed to save measurements. Please check all required fields are filled.');
      }
    }
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Strawberry Data Entry
        </Typography>
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Farm</InputLabel>
            <Select
              value={selectedFarm?.id || ''}
              onChange={handleFarmChange}
              label="Farm"
              disabled={!farms.length}
            >
              {farms.length ? (
                farms.map((farm) => (
                  <MenuItem key={farm.id} value={farm.id}>
                    {farm.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No farms available
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Zone</InputLabel>
            <Select
              value={selectedZone || ''}
              onChange={handleZoneChange}
              label="Zone"
              disabled={!selectedFarm || !zones.length}
            >
              {zones.length ? (
                zones.map((zone) => (
                  <MenuItem key={zone.id} value={zone.id}>
                    {zone.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No zones available
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
        <Grid container spacing={3}>
          {measurements.map((measurement, idx) => (
            <React.Fragment key={measurement.id}>
              {/* Time & Date Group */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Time & Date
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Date"
                      type="date"
                      value={measurement.date}
                      onChange={(e) => handleInputChange(idx, 'date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Time"
                      type="time"
                      value={measurement.time}
                      onChange={(e) => handleInputChange(idx, 'time', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Environmental Conditions Group */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Environmental Conditions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="EC in Coir Bag"
                      value={measurement.ec_coir_bag}
                      onChange={(e) => handleInputChange(idx, 'ec_coir_bag', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Moisture VMC %"
                      value={measurement.moisture_vmc}
                      onChange={(e) => handleInputChange(idx, 'moisture_vmc', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Coir Bag Temp. °C"
                      value={measurement.coir_bag_temp}
                      onChange={(e) => handleInputChange(idx, 'coir_bag_temp', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Air Temp. °C"
                      value={measurement.air_temp}
                      onChange={(e) => handleInputChange(idx, 'air_temp', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Irrigation Group */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Irrigation
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SBD"
                      value={measurement.sbd}
                      onChange={(e) => handleInputChange(idx, 'sbd', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Cycles"
                      value={measurement.cycles}
                      onChange={(e) => handleInputChange(idx, 'cycles', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Drip Volume"
                      value={measurement.drip_volume}
                      onChange={(e) => handleInputChange(idx, 'drip_volume', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Drip EC"
                      value={measurement.drip_ec}
                      onChange={(e) => handleInputChange(idx, 'drip_ec', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Drip pH"
                      value={measurement.drip_ph}
                      onChange={(e) => handleInputChange(idx, 'drip_ph', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Drainage Group */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Drainage
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Drain Volume"
                      value={measurement.drain_volume}
                      onChange={(e) => handleInputChange(idx, 'drain_volume', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Drain EC"
                      value={measurement.drain_ec}
                      onChange={(e) => handleInputChange(idx, 'drain_ec', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Drain pH"
                      value={measurement.drain_ph}
                      onChange={(e) => handleInputChange(idx, 'drain_ph', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Run Off %"
                      value={measurement.run_off_percentage}
                      onChange={(e) => handleInputChange(idx, 'run_off_percentage', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>
            </React.Fragment>
          ))}
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default DataEntryPage;
